/**
 * Step 5 — Review and Save.
 *
 * Read-only summary of everything the wizard has collected, plus the
 * Monitor name input + the actual Save button that POSTs to
 * `/v1/admin/import-jobs`.
 *
 * Field values are resolved to human-readable labels where possible —
 * Teams id → team name (via the same `useCapabilityOptions` resolver that
 * Step 2's MultiSelectField uses), Collection id → collection name (via
 * the existing useCollections hook), select values → their declared
 * `field.options[].label`. Raw values are shown as a fallback so a missing
 * lookup doesn't blank the row.
 */
import React, { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import {
  useCapabilityOptions,
  useCreateImportJob,
} from '../../../../hooks/useImportCapabilities';
import { fetchWithAuth } from '../../../../utils/api';
import { useCollections } from '../../../../hooks/useRecords';
import type { ImportCapabilityOptionsResponse } from '../../../../types/importCapabilities';
import type {
  CapabilityField,
  ImportCapability,
  ImportCapabilitySkill,
  ImportJobResponse,
} from '../../../../types/importCapabilities';

interface ReviewStepProps {
  skill: ImportCapabilitySkill;
  capability: ImportCapability;
  sourceConfig: Record<string, unknown>;
  destinationConfig: Record<string, unknown>;
  scheduleConfig: Record<string, unknown>;
  monitorName: string;
  onChangeMonitorName: (name: string) => void;
  onBack: () => void;
  onSaved: (job: ImportJobResponse) => void;
}

const CADENCE_LABEL: Record<string, string> = {
  every_30m: 'Every 30 minutes',
  every_1h: 'Hourly',
  daily: 'Daily',
};

const DESTINATION_SKILL = 'good-outcomes-collections';
const DESTINATION_CAPABILITY = 'import_to_collection';

const formatPrimitive = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  return String(value);
};

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-right text-sm text-slate-900">{value}</span>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <h3 className="mb-2 text-sm font-semibold text-slate-900">{title}</h3>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);

/** Resolve a single static `select` / `date_window` value to its label. */
const resolveStaticLabel = (
  field: CapabilityField,
  value: unknown,
): string => {
  if (value === undefined || value === null || value === '') return '—';
  const match = (field.options ?? []).find(
    (o) => o.value === String(value),
  );
  return match?.label ?? String(value);
};

/**
 * Resolve an array of ids to a comma-joined list of labels, using the
 * provided lookup (label-by-value). Falls back to the raw id when the
 * lookup misses, so a slow/failed resolver doesn't blank the row.
 */
const joinLabels = (
  values: unknown,
  lookup: Record<string, string>,
): string => {
  if (!Array.isArray(values) || values.length === 0) return '—';
  return values.map((v) => lookup[String(v)] ?? String(v)).join(', ');
};

const ReviewStep: React.FC<ReviewStepProps> = ({
  skill,
  capability,
  sourceConfig,
  destinationConfig,
  scheduleConfig,
  monitorName,
  onChangeMonitorName,
  onBack,
  onSaved,
}) => {
  const sourceFields: CapabilityField[] = capability.config_schema?.fields ?? [];

  const destinationMode =
    destinationConfig.mode === 'new' ? 'new' : 'existing';
  const cadence =
    typeof scheduleConfig.cadence === 'string' ? scheduleConfig.cadence : '';
  const cadenceLabel = CADENCE_LABEL[cadence] ?? cadence ?? '—';
  const timeOfDay = formatPrimitive(scheduleConfig.time_of_day);
  const timezone = formatPrimitive(scheduleConfig.timezone ?? 'UTC');

  // Resolve the async multi_select fields (Intercom teams etc.) to their
  // labels. For v1 there's at most one async multi_select per source
  // capability, so we resolve the first (if any) and join its labels.
  // If a future source declares multiple async multi_selects we'll extend.
  const firstAsync = useMemo(
    () =>
      sourceFields.find(
        (f) =>
          f.type === 'multi_select' &&
          Boolean(f.options_provider?.script || f.options_provider?.backend_tool),
      ),
    [sourceFields],
  );
  const asyncOptionsQuery = useCapabilityOptions({
    skill_name: skill.skill_name,
    capability_id: capability.capability_id,
    field_id: firstAsync?.id ?? '',
    // Same depends_on plumbing as the field renderers — scope the cache
    // key to only the resolver's declared deps.
    depends_on: firstAsync?.options_provider?.depends_on,
    enabled: Boolean(firstAsync),
  });
  const asyncLookup: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of asyncOptionsQuery.data?.options ?? []) {
      map[opt.value] = opt.label;
    }
    return map;
  }, [asyncOptionsQuery.data]);

  // Single-choice `async_select` fields (e.g. DataEQ's account_code +
  // brand_id) also need label resolution — without it the Review step shows
  // raw IDs ("BEAB26AA" / "277891") instead of the human labels the user
  // picked ("ABSA_Sandbox" / "Absa Test Page"). The existing multi_select
  // resolution above only covers the Intercom-shaped case; this loop covers
  // every async_select field, threading the live sourceConfig as the
  // resolver context so dependent fields (brand_id depends on account_code)
  // resolve correctly.
  const asyncSingleFields = useMemo(
    () =>
      sourceFields.filter(
        (f) =>
          f.type === 'async_select' &&
          Boolean(f.options_provider?.script || f.options_provider?.backend_tool),
      ),
    [sourceFields],
  );
  const asyncSingleQueries = useQueries({
    queries: asyncSingleFields.map((f) => {
      // Same dep-scoping logic as useCapabilityOptions: pluck only the
      // sourceConfig entries this resolver actually consumes so unrelated
      // keystrokes on sibling fields don't invalidate every label cache.
      const declaredDeps = f.options_provider?.depends_on;
      const scopedContext: Record<string, unknown> = declaredDeps
        ? Object.fromEntries(
            declaredDeps
              .filter((k) => k in (sourceConfig ?? {}))
              .map((k) => [k, (sourceConfig ?? {})[k]]),
          )
        : sourceConfig ?? {};
      return {
        queryKey: [
          'admin',
          'import-capabilities',
          'options',
          skill.skill_name,
          capability.capability_id,
          f.id,
          JSON.stringify(scopedContext),
        ],
        queryFn: async () =>
          (await fetchWithAuth(
            `/v1/admin/import-capabilities/${encodeURIComponent(skill.skill_name)}/${encodeURIComponent(capability.capability_id)}/options`,
            {
              method: 'POST',
              body: JSON.stringify({
                field_id: f.id,
                context: sourceConfig ?? {},
              }),
            },
          )) as ImportCapabilityOptionsResponse,
        staleTime: 60_000,
        retry: false,
      };
    }),
  });
  const asyncSingleLookup: Record<string, Record<string, string>> = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    asyncSingleFields.forEach((f, idx) => {
      const data = asyncSingleQueries[idx]?.data;
      const inner: Record<string, string> = {};
      for (const opt of data?.options ?? []) {
        inner[opt.value] = opt.label;
      }
      map[f.id] = inner;
    });
    return map;
  }, [asyncSingleFields, asyncSingleQueries]);

  // Collection name lookup for mode='existing'.
  const collectionsQuery = useCollections();
  const collectionLookup: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of collectionsQuery.data?.collections ?? []) {
      map[c.id] = c.name;
    }
    return map;
  }, [collectionsQuery.data]);

  // Per-field rows for the Configure section with resolved labels.
  const sourceRows = useMemo(() => {
    return sourceFields.map((f) => {
      const raw = sourceConfig[f.id];
      let display: string;
      if (f.type === 'select' || f.type === 'date_window') {
        display = resolveStaticLabel(f, raw);
      } else if (f.type === 'async_select') {
        const lookup = asyncSingleLookup[f.id] || {};
        const rawStr = typeof raw === 'string' ? raw : '';
        // Falls back to the raw value when the async lookup hasn't returned
        // yet (still pending) — the row stays populated rather than blanking
        // on the way to settled state.
        display = (rawStr && lookup[rawStr]) || formatPrimitive(raw);
      } else if (f.type === 'multi_select') {
        if (f.options_provider) {
          display = joinLabels(raw, asyncLookup);
        } else {
          const staticLookup = (f.options ?? []).reduce<Record<string, string>>(
            (acc, o) => ({ ...acc, [o.value]: o.label }),
            {},
          );
          display = joinLabels(raw, staticLookup);
        }
      } else if (f.type === 'switch') {
        display = formatPrimitive(raw);
      } else {
        display = formatPrimitive(raw);
      }
      return { label: f.label || f.id, value: display };
    });
  }, [sourceFields, sourceConfig, asyncLookup, asyncSingleLookup]);

  // Save handler — POST /v1/admin/import-jobs.
  const createJob = useCreateImportJob();
  const trimmedName = monitorName.trim();
  const canSave = trimmedName.length > 0 && !createJob.isPending;
  const saveErrorMessage =
    createJob.error instanceof Error ? createJob.error.message : null;

  const handleSave = () => {
    if (!canSave) return;
    createJob.mutate(
      {
        name: trimmedName,
        source_skill_name: skill.skill_name,
        source_capability_id: capability.capability_id,
        source_config: sourceConfig,
        destination_skill_name: DESTINATION_SKILL,
        destination_capability_id: DESTINATION_CAPABILITY,
        destination_config: destinationConfig,
        schedule_config: scheduleConfig,
      },
      { onSuccess: (job) => onSaved(job) },
    );
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">Review and save</h2>
        <p className="text-sm text-slate-600">
          Give your monitor a name, then confirm the settings below.
        </p>
      </header>

      <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-4">
        <Label htmlFor="review-monitor-name">
          Monitor name <span className="ml-1 text-red-500">*</span>
        </Label>
        <Input
          id="review-monitor-name"
          value={monitorName}
          onChange={(e) => onChangeMonitorName(e.target.value)}
          placeholder="e.g. Intercom — Claims hourly monitor"
        />
        <p className="text-xs text-slate-500">
          Shown in the monitor list and admin tooling. Distinct from the
          destination collection's name below.
        </p>
      </div>

      <Section title="Source">
        <SummaryRow
          label="Skill"
          value={skill.name || skill.skill_name}
        />
        <SummaryRow label="Capability" value={capability.label} />
      </Section>

      <Section title="Configure">
        {sourceRows.length === 0 ? (
          <p className="py-1 text-xs text-slate-500">
            This source has no configurable options.
          </p>
        ) : (
          sourceRows.map((row) => (
            <SummaryRow key={row.label} label={row.label} value={row.value} />
          ))
        )}
      </Section>

      <Section title="Destination">
        <SummaryRow
          label="Mode"
          value={destinationMode === 'new' ? 'New collection' : 'Existing collection'}
        />
        {destinationMode === 'existing' ? (
          <SummaryRow
            label="Collection"
            value={
              typeof destinationConfig.collection_id === 'string'
                ? collectionLookup[destinationConfig.collection_id] ??
                  destinationConfig.collection_id
                : '—'
            }
          />
        ) : (
          <>
            <SummaryRow
              label="Collection name"
              value={formatPrimitive(destinationConfig.collection_name)}
            />
            <SummaryRow
              label="Billing tag"
              value={formatPrimitive(destinationConfig.billing_tag)}
            />
            <SummaryRow
              label="Complaint detection"
              value={formatPrimitive(destinationConfig.complaint_detection_enabled)}
            />
            <SummaryRow
              label="Vulnerability detection"
              value={formatPrimitive(destinationConfig.vulnerability_detection_enabled)}
            />
          </>
        )}
      </Section>

      <Section title="Schedule">
        <SummaryRow label="Cadence" value={cadenceLabel} />
        {cadence === 'daily' ? (
          <SummaryRow label="Daily time (UTC)" value={timeOfDay} />
        ) : null}
        <SummaryRow label="Timezone" value={timezone} />
      </Section>

      {saveErrorMessage ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
          Could not save the monitor: {saveErrorMessage}
        </p>
      ) : null}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack} disabled={createJob.isPending}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!trimmedName ? (
            <span className="text-xs text-slate-500">
              Enter a monitor name to save
            </span>
          ) : null}
          <Button onClick={handleSave} disabled={!canSave}>
            {createJob.isPending ? 'Saving…' : 'Save monitor'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
